import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT, UserService } from '@cfp/core';
import { ReportCoverPage, ReportFooter } from '@cfp/features-shared/reports/report';
import { EnergyLabelService } from '@cfp/shared/energy-label';
import { PortfolioLayoutPageBase } from '@cfp/shared/portfolio/layout';
import { PrintableContent } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import {
  CustomerByPortfolioService,
  EnergyLabelTypeDTO,
  LabelGroupDTO,
  OwnershipStatusDTO,
  PortfolioBuildingsRoadmapDTO,
  PortfolioNotesService,
  PortfolioRoadmapReportService
} from 'csr-asset-app';
import { catchError, combineLatest, map, mergeMap, of } from 'rxjs';
import { NotesList } from '../notes';
import {
  ReportAppendicesPages,
  ReportBuidingsPages,
  ReportEnergyLabelsPages,
  ReportInvestmentPages,
  ReportPage
} from './pages';

@Component({
  selector: 'cfp-portfolio-buildings-roadmap-report',
  templateUrl: 'portfolio-buildings-roadmap-report.html',
  styleUrls: ['portfolio-buildings-roadmap-report.scss'],
  imports: [
    PrintableContent,
    ReportPage,
    ReportCoverPage,
    ReportFooter,
    ReportBuidingsPages,
    ReportEnergyLabelsPages,
    ReportInvestmentPages,
    ReportAppendicesPages,
    NotesList,
    TranslateModule,
    NgTemplateOutlet
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortfolioBuidingsRoadmapReport extends PortfolioLayoutPageBase {
  private readonly environment = inject(ENVIRONMENT);
  private readonly customerByPortfolioService = inject(CustomerByPortfolioService);
  private readonly userService = inject(UserService);
  private readonly portfolioRoadmapReportService = inject(PortfolioRoadmapReportService);
  private readonly energyLabelService = inject(EnergyLabelService);
  private readonly portfolioNotesService = inject(PortfolioNotesService);

  protected readonly coverLogo = this.environment.images.logoReport || this.environment.images.logoNavbar;
  protected readonly headerLogo = `${this.environment.customerAssetsDir}img/${this.coverLogo}`;
  protected readonly showExplanationPage = this.environment.components.portfolioReportShowExplanationPage === true;
  protected readonly showIntroductionPage = this.environment.components.portfolioReportShowIntroductionPage !== false;

  protected currentDate = new Date();

  protected readonly customerName = toSignal(
    toObservable(this.portfolioId).pipe(
      mergeMap((portfolioId) => {
        if (!portfolioId) {
          return of('');
        }
        return this.customerByPortfolioService
          .v2CustomerByPortfolioIdGet(portfolioId)
          .pipe(map((customer) => customer.name));
      }),
      catchError(() => of(''))
    ),
    {
      initialValue: ''
    }
  );

  protected readonly hasEnergyLabelRequirementLicense = toSignal(
    this.userService.hasLicense('energy_label_requirement')
  );

  protected readonly hasPortfolioNotesLicense = toSignal(this.userService.hasLicense('portfolio_notes'));

  protected readonly portfolioBuildingsRoadmap = toSignal<PortfolioBuildingsRoadmapDTO>(
    toObservable(this.portfolioId).pipe(
      mergeMap((portfolioId) => {
        if (!portfolioId) {
          return of(undefined);
        }
        return this.portfolioRoadmapReportService.v2PortfoliosPortfolioIdRoadmapReportGet(portfolioId).pipe(
          map((portfolioBuildingsRoadmap) => {
            // Currently this report only supports EPC
            portfolioBuildingsRoadmap.buildings.sort(
              (a, b) =>
                (a.energyLabels
                  ? this.energyLabelService.getSortIndex(LabelGroupDTO.EPC, a.energyLabels.currentEnergyLabel.label)
                  : 1000) -
                (b.energyLabels
                  ? this.energyLabelService.getSortIndex(LabelGroupDTO.EPC, b.energyLabels.currentEnergyLabel.label)
                  : 1000)
            );
            return portfolioBuildingsRoadmap;
          })
        );
      })
    )
  );

  protected readonly portfolioNotes = toSignal(
    combineLatest([toObservable(this.portfolioId), toObservable(this.hasPortfolioNotesLicense)]).pipe(
      mergeMap(([portfolioId, hasPortfolioNotesLicense]) => {
        if (!portfolioId || !hasPortfolioNotesLicense) {
          return of(undefined);
        }
        return this.portfolioNotesService.v2PortfoliosPortfolioIdNotesGet(portfolioId);
      })
    )
  );

  protected readonly buildingsWithPlan = computed(() =>
    this.portfolioBuildingsRoadmap()?.buildings.filter((buildingData) => buildingData.roadmap != null)
  );

  protected readonly buildingsForAppendices = computed(() =>
    this.portfolioBuildingsRoadmap()?.buildings.filter((buildingData) => {
      if (buildingData.roadmap != null) {
        // Has a roadmap
        return true;
      }
      if (buildingData.building.ownershipStatus === OwnershipStatusDTO.PLANSTOSELLBEFORE2030) {
        // Ownership status is Plan to sell before 2030
        return true;
      }
      if (
        buildingData.energyLabels?.currentEnergyLabel?.labelType === EnergyLabelTypeDTO.CALCULATEDCURRENT &&
        buildingData.energyLabels?.currentEnergyLabel?.label[0] === 'A'
      ) {
        // Current energy label is calculated and is A
        return true;
      }
      return false;
    })
  );

  protected readonly years = computed(() =>
    this.portfolioBuildingsRoadmap()?.totalInvestmentPerYear.map(
      (totalInvestmentPerYear) => totalInvestmentPerYear.year
    )
  );
}
